import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import * as pdfjsLib from "pdfjs-dist";
import ReactLoading from "react-loading";
import { createWorker } from "tesseract.js";
import Img from "./Img";
import import_logo from "../assets/import.svg";
import download_logo from "../assets/download.svg";

/*
  The PDF.js worker is served unchanged from Frontend/public/static/ (copied
  from node_modules/pdfjs-dist/build/pdf.worker.min.mjs), which bypasses the
  CRA/webpack/Babel pipeline entirely.
*/
pdfjsLib.GlobalWorkerOptions.workerSrc = "/static/pdf.worker.min.mjs";
/*
  ── PITT BRAND COLOR PALETTE (same as BioRECIPE) ──
  Primary:   Royal Blue #003594, Gold #FFB81C
  Secondary: New Sky #DBEEFF, Gray #C8C9C7, White #FFFFFF,
             Medium Blue #00205B, Bronze #B87333, Black #000000
  Accent:    Golden Haze #FFE939, Light Blue #66B2E3, Light Green #00AD6E,
             Orange #F7911E, Infrared #FF5B45, White Peach #FFDECD, Merlot #770538
*/

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Mystery+Quest&display=swap');

* { margin: 0; padding: 0; box-sizing: border-box; }

body {
  font-family: Arial, sans-serif;
  background: #f4f5f7;
  min-height: calc(100vh - 64px);
}

.page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
}

/* ── Top header (no sidebar) ── */
.top-header {
  background-color: #00205B;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.top-header-logo {
  width: 10px;
  height: 10px;
  background-color: #FFB81C;
  border-radius: 50%;
  flex-shrink: 0;
}

.top-header-title {
  font-size: 26px;
  font-family: 'Mystery Quest', cursive;
  color: #FFFFFF;
  letter-spacing: 1px;
  line-height: 1;
}

/* ── Main ── */
.main-area {
  flex: 1;
  padding: 24px 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
  min-width: 0;
}

.main-area-full {
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
}

/* ── Note toggle button (always visible, right side) ── */
.note-toggle-btn {
  position: fixed;
  top: 140px;
  right: 0;
  background: #FFB81C;
  color: #00205B;
  border: none;
  border-radius: 8px 0 0 8px;
  padding: 12px 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: -2px 2px 10px rgba(0,0,0,0.15);
  z-index: 60;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  letter-spacing: 0.03em;
  transition: background 0.15s;
}
.note-toggle-btn:hover { background: #F7911E; color: #fff; }
.note-toggle-btn.active { background: #00205B; color: #fff; }

/* ── Note panel (overlay, appears on click) ── */
.side-note-panel {
  position: fixed;
  top: 110px;
  right: 24px;
  width: 300px;
  max-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 2px solid #FFB81C;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.2);
  z-index: 65;
  animation: notePanelIn 0.15s ease-out;
}

@keyframes notePanelIn {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

.side-note-header {
  background: #00205B;
  color: #fff;
  padding: 10px 14px;
  border-radius: 6px 6px 0 0;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.side-note-header-actions { display: flex; align-items: center; gap: 6px; }

.side-note-clear {
  border: none;
  background: rgba(255,255,255,0.15);
  color: #fff;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  cursor: pointer;
  font-weight: 600;
}
.side-note-clear:hover { background: rgba(255,255,255,0.3); }

.side-note-close {
  border: none;
  background: none;
  color: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.side-note-close:hover { color: #FFB81C; }

.side-note-textarea {
  flex: 1;
  min-height: 220px;
  border: none;
  outline: none;
  resize: none;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.5;
  color: #00205B;
  font-family: Arial, sans-serif;
  border-radius: 0 0 6px 6px;
}

.side-note-saved {
  font-size: 10px;
  color: #00AD6E;
  padding: 4px 14px 8px;
  font-weight: 600;
}

.page-heading { font-size: 20px; font-weight: 700; color: #00205B; flex-shrink: 0; }
.page-subheading { font-size: 13px; color: #555; margin-top: 2px; }

/* ── Plain Table ── */
.table-outer-wrapper {
  overflow: auto;
  border: 2px solid #C8C9C7;
  border-radius: 6px;
  background: #fff;
  max-height: clamp(250px, 60vh, 75vh);
  flex-shrink: 0;
  position: relative;
}

.bio-table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
  font-size: 12px;
}

.bio-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #003594;
  color: #fff;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  border-right: 1px solid rgba(255,255,255,0.15);
  white-space: nowrap;
  vertical-align: middle;
}

.bio-table thead th.key-th { background-color: #FFB81C; color: #00205B; }
.bio-table thead th.status-th { background-color: #C8C9C7; color: #00205B; }
.bio-table thead th.base-th { background-color: #00205B; }
.bio-table thead th.model-th { background-color: #003594; }
.bio-table thead th.group-th {
  text-align: center;
  background-color: #66B2E3;
  color: #00205B;
}

.bio-table tbody tr { border-bottom: 1px solid #e5e7eb; }
.bio-table tbody tr:nth-child(even) td { background-color: #fafbfc; }
.bio-table tbody tr:hover td { background-color: #DBEEFF; }

.bio-table tbody td {
  padding: 6px 12px;
  color: #000;
  border-right: 1px solid #C8C9C7;
  line-height: 1.4;
  vertical-align: middle;
  white-space: nowrap;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bio-table tbody td.key-cell { font-weight: 700; color: #00205B; background-color: #FFF8E1 !important; }

.bio-table tbody td.diff-cell {
  background-color: #FFDECD !important;
  color: #770538;
  font-weight: 700;
  border-left: 2px solid #FF5B45;
  border-right: 2px solid #FF5B45;
}

.bio-table tbody td.missing-cell {
  color: #999;
  font-style: italic;
  background-color: #f0f0f0 !important;
}

.bio-table tbody tr.row-only-base td { background-color: #DBEEFF !important; }
.bio-table tbody tr.row-only-model td { background-color: #FFF3E0 !important; }

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
}
.status-match { background: #DFF5EA; color: #00AD6E; }
.status-diff { background: #FFDECD; color: #770538; }
.status-onlybase { background: #DBEEFF; color: #003594; }
.status-onlymodel { background: #FFF3E0; color: #B87333; }

/* ── Buttons ── */
.action-btn {
  padding: 4px 10px;
  border: 1px solid #00205B;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.2s;
  margin-right: 4px;
}

.edit-btn { background: #DBEEFF; color: #00205B; }
.edit-btn:hover { background: #66B2E3; color: #fff; }
.insert-btn { background: #FFB81C; color: #00205B; }
.insert-btn:hover { background: #F7911E; color: #fff; }
.delete-btn { background: #FF5B45; color: #000; }
.delete-btn:hover { background: #770538; color: #fff; }
.inhibit-btn { background: #770538; color: #fff; }
.inhibit-btn:hover { background: #4a0322; color: #fff; }
.inhibit-btn.active { background: #4a0322; box-shadow: inset 0 0 0 2px #FFB81C; }
.inhibit-btn.active:hover { background: #2e0116; }
.save-btn { background: #00AD6E; color: #000; }
.save-btn:hover { background: #008a58; color: #fff; }
.cancel-btn { background: #C8C9C7; color: #000; }
.cancel-btn:hover { background: #999; color: #fff; }

.row-num-td {
  text-align: center;
  color: #555;
  font-weight: 600;
  width: 36px;
  min-width: 36px;
}

.actions-td {
  text-align: center;
  white-space: nowrap;
  min-width: 290px;
  width: 290px;
}

.bio-table tbody tr.editing td { background-color: #DBEEFF; }
.bio-table tbody tr.editing { position: relative; z-index: 5; }

.edit-input {
  width: 100%;
  padding: 3px 6px;
  font-size: 12px;
  border: 1px solid #003594;
  border-radius: 4px;
  outline: none;
  background: #fff;
}
.edit-input:focus { border-color: #003594; box-shadow: 0 0 0 2px rgba(0,53,148,0.15); }

.edit-textarea {
  width: 100%;
  resize: none;
  height: 64px;
  line-height: 1.5;
  font-size: 12px;
  border: 1px solid #003594;
  border-radius: 4px;
  outline: none;
  background: #fff;
  padding: 3px 6px;
}
.edit-textarea:focus { border-color: #003594; box-shadow: 0 0 0 2px rgba(0,53,148,0.15); }

.bio-table tbody td.statements-cell {
  cursor: pointer;
  max-width: 320px;
}
.bio-table tbody td.statements-cell.expanded {
  white-space: normal;
  word-break: break-word;
  vertical-align: top;
  max-width: 480px;
}

/* ── Multi-select dropdown (portal-rendered) ── */
.edit-multiselect { position: relative; width: 100%; }

.edit-multiselect-display {
  width: 100%;
  padding: 3px 6px;
  font-size: 12px;
  border: 1px solid #003594;
  border-radius: 4px;
  outline: none;
  background: #fff;
  color: #000;
  cursor: pointer;
  min-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.edit-multiselect-display.placeholder { color: #888; }

.edit-multiselect-panel {
  z-index: 9999;
  background: #fff;
  border: 1px solid #003594;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.18);
  max-height: 220px;
  overflow-y: auto;
  padding: 4px 0;
}

.edit-multiselect-search-wrap {
  padding: 4px 8px 6px;
  position: sticky;
  top: 0;
  background: #fff;
  border-bottom: 1px solid #C8C9C7;
}

.edit-multiselect-search {
  width: 100%;
  padding: 4px 6px;
  font-size: 12px;
  border: 1px solid #66B2E3;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
}
.edit-multiselect-search:focus { border-color: #003594; box-shadow: 0 0 0 2px rgba(0,53,148,0.15); }

.edit-multiselect-empty { padding: 8px 10px; font-size: 12px; color: #999; font-style: italic; }

.edit-multiselect-option {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}
.edit-multiselect-option:hover { background: #DBEEFF; }
.edit-multiselect-option input { cursor: pointer; }

.edit-multiselect-clear { border-top: 1px solid #C8C9C7; color: #B87333; font-style: italic; }

.add-row-btn {
  padding: 10px 22px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.25s;
  background: #003594;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;
}
.add-row-btn:hover { background: #00205B; box-shadow: 0 2px 8px rgba(0,53,148,0.3); }
.add-row-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.table-info { font-size: 12px; color: #555; padding: 4px 0; display: flex; justify-content: space-between; flex-shrink: 0; }

/* ── Footer ── */
.app-footer {
  background-color: #00205B;
  color: #DBEEFF;
  text-align: center;
  padding: 14px 24px;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}
.app-footer a { color: #FFB81C; text-decoration: none; transition: color 0.2s; }
.app-footer a:hover { color: #FFE939; }

/* ── Empty state ── */
.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px 0; color: #C8C9C7; font-size: 14px; gap: 12px; }
.empty-icon { font-size: 32px; opacity: 0.5; }

/* ── Upload panel (reused for both base/model files and PDF) ── */
.upload-panel {
  background: #DBEEFF;
  border: 2px dashed #003594;
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex-shrink: 0;
}

.upload-panel-title { font-size: 14px; font-weight: 700; color: #00205B; display: flex; align-items: center; gap: 8px; }
.upload-panel-hint { font-size: 11px; color: #003594; margin-top: -4px; }

.upload-slots {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
}

.upload-slot {
  flex: 1;
  min-width: 240px;
  background: #fff;
  border: 1px solid #C8C9C7;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upload-slot.filled { border-color: #00AD6E; }

.upload-slot-label {
  font-size: 12px;
  font-weight: 700;
  color: #00205B;
  display: flex;
  align-items: center;
  gap: 6px;
}

.upload-slot-tag {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 1px 6px;
  border-radius: 3px;
  color: #fff;
}
.upload-slot-tag.base { background: #003594; }
.upload-slot-tag.model { background: #F7911E; }
.upload-slot-tag.pdf { background: #00AD6E; }

.upload-btn {
  padding: 8px 18px;
  border: 1px solid #003594;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  background: #fff;
  color: #003594;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
}
.upload-btn:hover { background: #DBEEFF; border-color: #00205B; }

.upload-file-name {
  font-size: 11px;
  color: #00AD6E;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
}

.upload-row-count { font-size: 11px; color: #555; }

.import-file-input { display: none; }

/* ── Key column + toggle bar ── */
.compare-controls {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.compare-control-group { display: flex; align-items: center; gap: 8px; }
.compare-control-label { font-size: 12px; font-weight: 600; color: #00205B; }

.edit-select {
  padding: 5px 8px;
  font-size: 12px;
  border: 1px solid #003594;
  border-radius: 4px;
  outline: none;
  background: #fff;
  color: #000;
  cursor: pointer;
}

.toggle-switch {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #00205B;
  font-weight: 600;
  cursor: pointer;
}

/* ── Summary stat cards ── */
.summary-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.summary-card {
  flex: 1;
  min-width: 130px;
  border-radius: 8px;
  padding: 12px 16px;
  color: #fff;
}

.summary-card-value { font-size: 22px; font-weight: 800; line-height: 1.1; }
.summary-card-label { font-size: 11px; font-weight: 600; opacity: 0.9; margin-top: 2px; }

.summary-card.total { background: #00205B; }
.summary-card.match { background: #00AD6E; color: #00230f; }
.summary-card.diff { background: #FF5B45; }
.summary-card.onlybase { background: #003594; }
.summary-card.onlymodel { background: #F7911E; color: #3a2200; }

.download-btn {
  padding: 10px 22px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.25s;
  background: #00AD6E;
  color: #00205B;
  display: flex;
  align-items: center;
  gap: 6px;
}
.download-btn:hover { background: #008a58; color: #fff; }
.download-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.button-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0; }

/* ── Welcome ── */
.welcome-container { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; color: #C8C9C7; text-align: center; padding: 60px 20px; }
.welcome-icon { font-size: 56px; opacity: 0.4; }
.welcome-title { font-size: 22px; font-weight: 700; color: #003594; }
.welcome-subtitle { font-size: 14px; color: #00205B; max-width: 400px; line-height: 1.6; }

/* ── Error / Warning banners ── */
.error-banner {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  max-width: 480px;
  background-color: #FFDECD;
  border: 2px solid #FF5B45;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.error-banner-content { display: flex; flex-direction: column; gap: 8px; }
.error-banner-header { display: flex; justify-content: space-between; align-items: center; font-weight: bold; color: #770538; font-size: 14px; }
.error-banner-message { color: #00205B; font-size: 13px; line-height: 1.5; }
.error-banner-close { cursor: pointer; font-size: 20px; color: #FF5B45; background: none; border: none; padding: 0; line-height: 1; }
.error-banner-close:hover { color: #770538; }

/* ── PDF Plot Gallery: split layout (small list left, big preview right) ── */
.plot-gallery-split {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  min-height: 0;
}

.plot-thumb-col {
  width: 460px;
  min-width: 460px;
  max-height: clamp(560px, 85vh, 1100px);
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-content: start;
  gap: 14px;
  padding: 14px;
  border: 2px solid #C8C9C7;
  border-radius: 6px;
  background: #fafbfc;
  flex-shrink: 0;
}

.plot-preview-col {
  flex: 1;
  min-width: 0;
  max-height: clamp(560px, 85vh, 1100px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.plot-thumb {
  border: 2px solid #C8C9C7;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  transition: border-color 0.15s, box-shadow 0.15s;
  flex-shrink: 0;
}

.plot-thumb:hover { border-color: #66B2E3; }

.plot-thumb.selected {
  border-color: #FFB81C;
  box-shadow: 0 0 0 2px rgba(255,184,28,0.4);
}

.plot-thumb img { width: 100%; display: block; }

.plot-thumb-caption {
  font-size: 11px;
  font-weight: 600;
  color: #00205B;
  text-align: center;
  padding: 4px 2px;
  border-top: 1px solid #C8C9C7;
  background: #fafbfc;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.plot-thumb-check {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #FFB81C;
  color: #00205B;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
}

.selected-plots-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.selected-plot-card {
  background: #fff;
  border: 1px solid #C8C9C7;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.selected-plot-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #003594;
  color: #fff;
}

.selected-plot-card-title { font-size: 13px; font-weight: 700; }

.selected-plot-card-actions { display: flex; gap: 6px; }

.icon-btn {
  border: none;
  background: rgba(255,255,255,0.15);
  color: #fff;
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 700;
}
.icon-btn:hover { background: rgba(255,255,255,0.3); }
.icon-btn.remove { background: #FF5B45; }
.icon-btn.remove:hover { background: #770538; }

.selected-plot-card-body {
  display: flex;
  justify-content: center;
  padding: 12px;
  background: #fafbfc;
  overflow: auto;
}

.selected-plot-card-body canvas { max-width: 100%; }

.zoom-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #00205B;
  font-weight: 600;
}

.zoom-row input[type="range"] { width: 160px; }
`;

// ─────────────────────────────────────────────
// FILE READING HELPERS
// ─────────────────────────────────────────────

const readTableFile = (file) =>
  new Promise((resolve, reject) => {
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (![".xlsx", ".xls", ".csv"].includes(ext)) {
      reject(new Error(`Unsupported file type "${ext}". Use .xlsx, .xls, or .csv.`));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        if (!json.length) {
          reject(new Error("No data rows found in file."));
          return;
        }
        const columns = Object.keys(json[0]);
        resolve({ fileName: file.name, columns, rows: json });
      } catch (err) {
        reject(new Error(err.message || "Failed to parse file."));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsArrayBuffer(file);
  });

// ─────────────────────────────────────────────
// COMPARE DATASETS PAGE
// ─────────────────────────────────────────────

const UploadSlot = ({ tag, tagLabel, label, accept, file, countLabel, onChoose, disabled, loading, loadingLabel }) => {
  const inputRef = useRef(null);
  return (
    <div className={`upload-slot ${file ? "filled" : ""}`}>
      <div className="upload-slot-label">
        <span className={`upload-slot-tag ${tag}`}>{tagLabel}</span>
        <span>{label}</span>
      </div>
      <input
        type="file"
        ref={inputRef}
        className="import-file-input"
        accept={accept}
        onChange={(e) => {
          const f = e.target.files[0];
          if (f) onChoose(f);
          e.target.value = "";
        }}
      />
      <button
        className="upload-btn"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
      >
        <Img src={import_logo} /> {file ? "Replace File" : "Choose File"}
      </button>
      {loading && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ReactLoading type="spin" color="#003594" height={16} width={16} />
          <span style={{ fontSize: 11, color: "#003594" }}>{loadingLabel || "Reading file..."}</span>
        </div>
      )}
      {file && !loading && (
        <div className="upload-file-name" title={file}>
          ✅ {file}
        </div>
      )}
      {countLabel && !loading && <div className="upload-row-count">{countLabel}</div>}
    </div>
  );
};


// Baked-in page -> element-name lookup for the 410-page GBM baseline plot PDF
// ("plot_baseline_GBM_biorecipe_..._decrease100.pdf"). Verified by OCR + visual spot-check
// against GBM_model.xlsx. Used only as a fallback: applied when an uploaded PDF has
// exactly 410 pages AND no element names are embedded in the PDF's text layer.
const GBM_BASELINE_PLOT_NAMES = {
  1: "AKT_gene", 2: "AKT_prot_act", 3: "AKT_prot", 4: "AMPK_gene", 5: "AMPK_prot_act", 6: "AMPK_prot", 7: "Apaf1_gene",
  8: "Apaf1_prot_act", 9: "Apaf1_prot", 10: "apoptosis", 11: "ASCL1_gene", 12: "ASCL1_prot_act", 13: "ASCL1_prot",
  14: "astro_diff", 15: "ATM_gene", 16: "ATM_prot_act", 17: "ATM_prot", 18: "ATP", 19: "AURA_B_gene", 20: "AURA_B_prot_act",
  21: "AURA_B_prot", 22: "Bad_gene", 23: "Bad_prot_act", 24: "Bad_prot", 25: "Bax_gene", 26: "Bax_prot_act", 27: "Bax_prot",
  28: "Bcl2_gene", 29: "Bcl2_prot_act", 30: "Bcl2_prot", 31: "BclXL_gene", 32: "BclXL_prot_act", 33: "BclXL_prot",
  34: "beta_cat_gene", 35: "beta_cat_prot_act", 36: "beta_cat_prot", 37: "c_myc_gene", 38: "c_myc_prot_act", 39: "c_myc_prot",
  40: "Casp3_6_7_gene", 41: "Casp3_6_7_prot_act", 42: "Casp3_6_7_prot", 43: "Casp8_gene", 44: "Casp8_prot_act", 45: "Casp8_prot",
  46: "Casp9_gene", 47: "Casp9_prot_act", 48: "Casp9_prot", 49: "CBL_gene", 50: "CBL_prot_act", 51: "CBL_prot", 52: "CD44_gene",
  53: "CD44_prot_act", 54: "CD44_prot", 55: "cdc25A_gene", 56: "cdc25A_prot_act_cyto", 57: "cdc25A_prot_act_nuc",
  58: "cdc25A_prot", 59: "CDK1_cycB_gene", 60: "CDK1_cycB_prot_act", 61: "CDK1_cycB_prot", 62: "CDK2_gene", 63: "CDK2_prot_act",
  64: "CDK2_prot", 65: "CDK6_gene", 66: "CDK6_prot_act", 67: "CDK6_prot", 68: "Cell_cycle_progression", 69: "Chk1_2_gene",
  70: "Chk1_2_prot_act", 71: "Chk1_2_prot", 72: "cycD_CDK4_6_prot_act", 73: "cycD_gene", 74: "cycD_prot_act", 75: "cycD_prot",
  76: "cycE_CDK2_prot_act", 77: "cycE_gene", 78: "cycE_prot_act", 79: "cycE_prot", 80: "CytoC_gene", 81: "CytoC_prot_act",
  82: "CytoC_prot", 83: "DELTA_gene", 84: "DELTA_prot_act", 85: "DELTA_prot", 86: "DKK_gene", 87: "DKK_prot_act",
  88: "DKK_prot", 89: "dna_damage", 90: "DVL_gene", 91: "DVL_prot_act", 92: "DVL_prot", 93: "E2F_gene", 94: "E2F_prot_act",
  95: "E2F_prot", 96: "EGF_gene", 97: "EGF_prot_act", 98: "EGF_prot", 99: "EGFR_gene", 100: "EGFR_prot_act", 101: "EGFR_prot",
  102: "eIF4E_gene", 103: "eIF4E_prot_act", 104: "eIF4E_prot", 105: "EIF4EBP1_gene", 106: "EIF4EBP1_prot_act", 107: "EIF4EBP1_prot",
  108: "EphA2_gene", 109: "EphA2_prot_act", 110: "EphA2_prot", 111: "ERBB2_gene", 112: "ERBB2_prot_act", 113: "ERBB2_prot",
  114: "ERBB3_gene", 115: "ERBB3_prot_act", 116: "ERBB3_prot", 117: "ERK_gene", 118: "ERK_prot_act", 119: "ERK_prot",
  120: "F_ACTIN_gene", 121: "F_ACTIN_prot_act", 122: "F_ACTIN_prot", 123: "FAS_gene", 124: "FAS_prot_act", 125: "FAS_prot",
  126: "FASL_gene", 127: "FASL_prot_act", 128: "FASL_prot", 129: "FOXO_gene", 130: "FOXO_prot_act", 131: "FOXO_prot",
  132: "FRINGE_gene", 133: "FRINGE_prot_act", 134: "FRINGE_prot", 135: "FRMD_gene", 136: "FRMD_prot_act", 137: "FRMD_prot",
  138: "GAB1_gene", 139: "GAB1_prot_act", 140: "GAB1_prot", 141: "GLI_gene", 142: "GLI_prot_act", 143: "GLI_prot",
  144: "Grb2_gene", 145: "Grb2_prot_act", 146: "Grb2_prot", 147: "GSK3B_gene", 148: "GSK3B_prot_act", 149: "GSK3B_prot",
  150: "HH_gene", 151: "HH_prot_act", 152: "HH_prot", 153: "Hif1a_gene", 154: "Hif1a_prot_act", 155: "Hif1a_prot",
  156: "Hypoxia", 157: "IkB_a_gene", 158: "IkB_a_prot_act", 159: "IkB_a_prot", 160: "IKK_gene", 161: "IKK_prot_act",
  162: "IKK_prot", 163: "INSR_gene", 164: "INSR_prot_act", 165: "INSR_prot", 166: "Insulin", 167: "IRS_gene", 168: "IRS_prot_act",
  169: "IRS_prot", 170: "JAK2_gene", 171: "JAK2_prot_act", 172: "JAK2_prot", 173: "KIBRA_gene", 174: "KIBRA_prot_act",
  175: "KIBRA_prot", 176: "KIF7_gene", 177: "KIF7_prot_act", 178: "KIF7_prot", 179: "LATS1_2_gene", 180: "LATS1_2_prot_act",
  181: "LATS1_2_prot", 182: "LRP_Fz_gene", 183: "LRP_Fz_prot_act", 184: "LRP_Fz_prot", 185: "MAP3K7_gene", 186: "MAP3K7_prot_act",
  187: "MAP3K7_prot", 188: "MDM2_gene", 189: "MDM2_prot_act", 190: "MDM2_prot", 191: "MDMX_gene", 192: "MDMX_prot_act",
  193: "MDMX_prot", 194: "MEK_gene", 195: "MEK_prot_act", 196: "MEK_prot", 197: "MER_gene", 198: "MER_prot_act",
  199: "MER_prot", 200: "Met_gene", 201: "Met_prot_act", 202: "Met_prot", 203: "MOB_gene", 204: "MOB_prot_act", 205: "MOB_prot",
  206: "MST1_2_gene", 207: "MST1_2_prot_act", 208: "MST1_2_prot", 209: "mTORc1_gene", 210: "mTORc1_prot_act", 211: "mTORc1_prot",
  212: "mTORc2_gene", 213: "mTORc2_prot_act", 214: "mTORc2_prot", 215: "MUT_CDKN2A", 216: "MUT_MDM2", 217: "MUT_PDGFRA",
  218: "MUT_PTEN", 219: "MYT1_gene", 220: "MYT1_prot_act", 221: "MYT1_prot", 222: "Nestin_gene", 223: "Nestin_prot_act",
  224: "Nestin_prot", 225: "neuronal_diff", 226: "NF1_gene", 227: "NF1_prot_act", 228: "NF1_prot", 229: "NF2_gene",
  230: "NF2_prot_act", 231: "NF2_prot", 232: "NLK_gene", 233: "NLK_prot_act", 234: "NLK_prot", 235: "NOTCH_gene",
  236: "NOTCH_prot_act", 237: "NOTCH_prot", 238: "NSB1_gene", 239: "NSB1_prot_act", 240: "NSB1_prot", 241: "NUMB_gene",
  242: "NUMB_prot_act", 243: "NUMB_prot", 244: "p15_gene", 245: "p15_prot_act", 246: "p15_prot", 247: "p16_gene",
  248: "p16_prot_act", 249: "p16_prot", 250: "p21_gene", 251: "p21_prot_act", 252: "p21_prot", 253: "p27_gene", 254: "p27_prot_act",
  255: "p27_prot", 256: "p50_p65_gene", 257: "p50_p65_prot_act", 258: "p50_p65_prot", 259: "p53_gene", 260: "p53_prot_act",
  261: "p53_prot", 262: "PARP1_gene", 263: "PARP1_prot_act", 264: "PARP1_prot", 265: "PDGF_gene", 266: "PDGF_prot_act",
  267: "PDGF_prot", 268: "PDGFRA_gene", 269: "PDGFRA_prot_act", 270: "PDGFRA_prot", 271: "PDK_gene", 272: "PDK_prot_act",
  273: "PDK_prot", 274: "PI3K_gene", 275: "PI3K_prot_act", 276: "PI3K_prot", 277: "PIAS3_gene", 278: "PIAS3_prot_act",
  279: "PIAS3_prot", 280: "PIP3", 281: "PKC_gene", 282: "PKC_prot_act", 283: "PKC_prot", 284: "PLC_gene", 285: "PLC_prot_act",
  286: "PLC_prot", 287: "PLK1_gene", 288: "PLK1_prot_act", 289: "PLK1_prot", 290: "PP2A_gene", 291: "PP2A_prot_act",
  292: "PP2A_prot", 293: "PRAS_gene", 294: "PRAS_prot_act", 295: "PRAS_prot", 296: "PRC1_gene", 297: "PRC1_prot_act",
  298: "PRC1_prot", 299: "Proliferation", 300: "Protein_synthesis", 301: "PTEN_gene", 302: "PTEN_prot_act", 303: "PTEN_prot",
  304: "Raf_dimer_gene", 305: "Raf_dimer_prot_act", 306: "Raf_dimer_prot", 307: "RAS_gene", 308: "RAS_prot_act",
  309: "RAS_prot", 310: "RASSF6_gene", 311: "RASSF6_prot_act", 312: "RASSF6_prot", 313: "Rb_gene", 314: "Rb_prot_act",
  315: "Rb_prot", 316: "REDD1_gene", 317: "REDD1_prot_act", 318: "REDD1_prot", 319: "Rheb_gene", 320: "Rheb_prot_act",
  321: "Rheb_prot", 322: "RIP1_gene", 323: "RIP1_prot_act", 324: "RIP1_prot", 325: "ROS", 326: "RPS6KA1_gene", 327: "RPS6KA1_prot_act",
  328: "RPS6KA1_prot", 329: "RPS6KB1_2_gene", 330: "RPS6KB1_2_prot_act", 331: "RPS6KB1_2_prot", 332: "S6_gene", 333: "S6_prot_act",
  334: "S6_prot", 335: "SAV1_gene", 336: "SAV1_prot_act", 337: "SAV1_prot", 338: "SERRATE_gene", 339: "SERRATE_prot_act",
  340: "SERRATE_prot", 341: "SHC_gene", 342: "SHC_prot_act", 343: "SHC_prot", 344: "SHP2_gene", 345: "SHP2_prot_act",
  346: "SHP2_prot", 347: "SKP2_gene", 348: "SKP2_prot_act", 349: "SKP2_prot", 350: "SMAC_gene", 351: "SMAC_prot_act",
  352: "SMAC_prot", 353: "SMAD2_3_4_prot_act", 354: "SMAD2_3_gene", 355: "SMAD2_3_prot_act", 356: "SMAD2_3_prot",
  357: "SMAD4_gene", 358: "SMAD4_prot", 359: "SOCS_gene", 360: "SOCS_prot_act", 361: "SOCS_prot", 362: "SOS_gene",
  363: "SOS_prot_act", 364: "SOS_prot", 365: "SPRY2_gene", 366: "SPRY2_prot_act", 367: "SPRY2_prot", 368: "SRC_gene",
  369: "SRC_prot_act", 370: "SRC_prot", 371: "STAT3_gene", 372: "STAT3_prot_act", 373: "STAT3_prot", 374: "Stemness",
  375: "SUFU_gene", 376: "SUFU_prot_act", 377: "SUFU_prot", 378: "TACE_gene", 379: "TACE_prot_act", 380: "TACE_prot",
  381: "TCF_LEF_gene", 382: "TCF_LEF_prot_act", 383: "TCF_LEF_prot", 384: "TGFB_gene", 385: "TGFB_prot_act", 386: "TGFB_prot",
  387: "TGFBR_gene", 388: "TGFBR_prot_act", 389: "TGFBR_prot", 390: "TNF_gene", 391: "TNF_prot_act", 392: "TNF_prot",
  393: "TNFR_gene", 394: "TNFR_prot_act", 395: "TNFR_prot", 396: "TSC1_2_gene", 397: "TSC1_2_prot_act", 398: "TSC1_2_prot",
  399: "VEGF_gene", 400: "VEGF_prot_act", 401: "VEGF_prot", 402: "VEGFR_gene", 403: "VEGFR_prot_act", 404: "VEGFR_prot",
  405: "Wnt_gene", 406: "Wnt_prot_act", 407: "Wnt_prot", 408: "XIAP_gene", 409: "XIAP_prot_act", 410: "XIAP_prot",
};

const PDF_THUMB_SCALE = 0.35;

// Lowercase and strip everything except letters/digits, so "akt gene",
// "AKT_gene" and "aktgene" all match the same plot.
const normalizeForSearch = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");

// ── Reading the element name printed at the top of each plot ──
const OCR_RENDER_SCALE = 3;   // ~216 dpi, enough for small plot titles
const TITLE_STRIP_HEIGHT = 0.16; // title lives in the top 16% of the page
const TITLE_STRIP_LEFT = 0.2;    // centered title: skip the outer 20% each side
const TITLE_STRIP_RIGHT = 0.8;

const cleanTitle = (raw) =>
  String(raw || "")
    .replace(/\s+/g, "")
    .replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, "");

// Crop the title area of a page onto a white canvas (with padding) for OCR.
const titleStripCanvas = async (page) => {
  const viewport = page.getViewport({ scale: OCR_RENDER_SCALE });
  const full = document.createElement("canvas");
  full.width = viewport.width;
  full.height = viewport.height;
  await page.render({ canvasContext: full.getContext("2d"), viewport }).promise;

  const sx = Math.round(viewport.width * TITLE_STRIP_LEFT);
  const sw = Math.round(viewport.width * (TITLE_STRIP_RIGHT - TITLE_STRIP_LEFT));
  const sh = Math.round(viewport.height * TITLE_STRIP_HEIGHT);
  const pad = 20;
  const strip = document.createElement("canvas");
  strip.width = sw + pad * 2;
  strip.height = sh + pad * 2;
  const ctx = strip.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, strip.width, strip.height);
  ctx.drawImage(full, sx, 0, sw, sh, pad, pad, sw, sh);
  return strip;
};

const renderPageToDataUrl = async (pdfDoc, pageNum, scale) => {
  const page = await pdfDoc.getPage(pageNum);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  const ctx = canvas.getContext("2d");
  await page.render({ canvasContext: ctx, viewport }).promise;
  return canvas.toDataURL("image/png");
};

const SelectedPlotCanvas = ({ pdfDoc, pageNum, zoom }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!pdfDoc || !canvasRef.current) return;
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: zoom });
      const canvas = canvasRef.current;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      if (!cancelled) await page.render({ canvasContext: ctx, viewport }).promise;
    })();
    return () => { cancelled = true; };
  }, [pdfDoc, pageNum, zoom]);

  return <canvas ref={canvasRef} />;
};

const PlotSection = ({ modelRows, modelColumns, modelFileName }) => {
  // ── PDF plot state ──
  const [pdfFileName, setPdfFileName] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [thumbnails, setThumbnails] = useState([]); // [{pageNum, dataUrl}]
  const [pageTitles, setPageTitles] = useState({}); // {pageNum: "AKT_gene"} from the text layer or OCR
  const [titlesProgress, setTitlesProgress] = useState({ done: 0, total: 0 });
  const runIdRef = useRef(0); // lets a newer upload cancel an older OCR run

  // Stop any running OCR loop when the component unmounts.
  useEffect(() => () => { runIdRef.current += 1; }, []);
  const [selectedPages, setSelectedPages] = useState([]); // ordered array of pageNum
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ done: 0, total: 0 });
  const [pdfError, setPdfError] = useState(null);
  const [zoom, setZoom] = useState(1.2);
  const [rowOffset, setRowOffset] = useState(0);
  const [plotSearch, setPlotSearch] = useState("");

  // For PDFs with no text layer (e.g. images converted with Preview): OCR the
  // title strip of every page and fill in names as they are read.
  const extractTitlesByOcr = useCallback(async (doc, runId) => {
    setTitlesProgress({ done: 0, total: doc.numPages });
    let worker = null;
    try {
      worker = await createWorker("eng");
      await worker.setParameters({
        tessedit_char_whitelist:
          "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-",
        tessedit_pageseg_mode: "7", // treat the strip as a single line of text
      });
      const found = {};
      for (let p = 1; p <= doc.numPages; p++) {
        if (runIdRef.current !== runId) return; // a newer upload replaced this one
        const page = await doc.getPage(p);
        const { data } = await worker.recognize(await titleStripCanvas(page));
        const title = cleanTitle(data.text);
        if (title) found[p] = title;
        if (p % 10 === 0 || p === doc.numPages) {
          if (runIdRef.current !== runId) return;
          setPageTitles({ ...found });
          setTitlesProgress({ done: p, total: doc.numPages });
        }
      }
    } catch (err) {
      if (runIdRef.current === runId) {
        setPdfError({ title: "Could not read plot names", message: err.message || "Name recognition failed." });
      }
    } finally {
      if (worker) await worker.terminate();
      if (runIdRef.current === runId) setTitlesProgress((t) => ({ ...t, done: t.total }));
    }
  }, []);

  const handlePdfChange = useCallback(async (file) => {
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (ext !== ".pdf") {
      setPdfError({ title: "Invalid file type", message: `Please upload a .pdf file. Got "${ext}".` });
      return;
    }
    setPdfLoading(true);
    setPdfError(null);
    setSelectedPages([]);
    setThumbnails([]);
    setPageTitles({});
    setTitlesProgress({ done: 0, total: 0 });
    const runId = ++runIdRef.current;
    setPdfProgress({ done: 0, total: 0 });
    try {
      const buf = await file.arrayBuffer();
      const doc = await pdfjsLib.getDocument({ data: buf }).promise;
      setPdfDoc(doc);
      setPdfFileName(file.name);
      setPdfProgress({ done: 0, total: doc.numPages });

      // Read element names embedded in each page's text layer (fast, no OCR).
      // PDFs made with make_plot_pdf.py carry the element name as invisible
      // text on every page. PDFs without a text layer simply yield no titles.
      const titles = {};
      for (let p = 1; p <= doc.numPages; p++) {
        const page = await doc.getPage(p);
        const tc = await page.getTextContent();
        const text = tc.items.map((it) => it.str).join("").replace(/\s+/g, " ").trim();
        if (text) titles[p] = text;
      }
      setPageTitles(titles);
      const needOcr = Object.keys(titles).length === 0;

      const thumbs = [];
      for (let p = 1; p <= doc.numPages; p++) {
        const dataUrl = await renderPageToDataUrl(doc, p, PDF_THUMB_SCALE);
        thumbs.push({ pageNum: p, dataUrl });
        // Show thumbnails as they render instead of waiting for all ~400+ pages
        if (p % 10 === 0 || p === doc.numPages) setThumbnails([...thumbs]);
        setPdfProgress({ done: p, total: doc.numPages });
      }
      setThumbnails(thumbs);

      // No text layer found: read the names off the plots with OCR. Not awaited,
      // so thumbnails stay usable while names fill in.
      if (needOcr) extractTitlesByOcr(doc, runId);
    } catch (err) {
      setPdfError({ title: "Could not read PDF", message: err.message || "Failed to load PDF." });
    } finally {
      setPdfLoading(false);
    }
  }, [extractTitlesByOcr]);

  const toggleSelect = (pageNum) => {
    setSelectedPages((prev) =>
      prev.includes(pageNum) ? prev.filter((p) => p !== pageNum) : [...prev, pageNum],
    );
  };

  const removeSelected = (pageNum) => {
    setSelectedPages((prev) => prev.filter((p) => p !== pageNum));
  };

  const moveSelected = (pageNum, dir) => {
    setSelectedPages((prev) => {
      const idx = prev.indexOf(pageNum);
      const next = [...prev];
      const swapWith = idx + dir;
      if (swapWith < 0 || swapWith >= next.length) return prev;
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
      return next;
    });
  };

  const clearSelection = () => setSelectedPages([]);

  // Label sources, in priority order:
  //   1. Names embedded in the PDF itself (text layer)
  //   2. The verified 410-page GBM baseline lookup
  //   3. Model row order, when the page count matches the model's row count
  //   4. "Page N"
  const hasTitles = Object.keys(pageTitles).length > 0;

  // Use the real page count, not thumbnails.length (which is partial while rendering).
  const usingBakedNames = !hasTitles && pdfDoc?.numPages === 410;

  const elementNameColumn = modelColumns.length
    ? modelColumns.includes("Element Name")
      ? "Element Name"
      : modelColumns[0]
    : null;
  const elementNames = useMemo(() => {
    if (!modelRows.length || !elementNameColumn) return [];
    return modelRows.map((r) => String(r[elementNameColumn] ?? "").trim());
  }, [modelRows, elementNameColumn]);

  const namesAligned =
    !hasTitles &&
    !usingBakedNames &&
    elementNames.length > 0 &&
    elementNames.length === thumbnails.length;

  const hasNames = hasTitles || usingBakedNames || namesAligned;

  const getPlotLabel = useCallback(
    (pageNum) => {
      if (pageTitles[pageNum]) return pageTitles[pageNum];
      if (usingBakedNames && GBM_BASELINE_PLOT_NAMES[pageNum]) return GBM_BASELINE_PLOT_NAMES[pageNum];
      if (namesAligned) {
        const idx = pageNum - 1 + rowOffset;
        if (idx >= 0 && idx < elementNames.length && elementNames[idx]) return elementNames[idx];
      }
      return `Page ${pageNum}`;
    },
    [pageTitles, usingBakedNames, namesAligned, elementNames, rowOffset],
  );

  const filteredThumbnails = useMemo(() => {
    const q = normalizeForSearch(plotSearch);
    if (!q) return thumbnails;
    return thumbnails.filter(
      (t) =>
        normalizeForSearch(getPlotLabel(t.pageNum)).includes(q) ||
        String(t.pageNum) === plotSearch.trim(),
    );
  }, [thumbnails, plotSearch, getPlotLabel]);

  return (
    <>
      {pdfError && (
        <div className="error-banner">
          <div className="error-banner-content">
            <div className="error-banner-header">
              <span>❌ {pdfError.title}</span>
              <button className="error-banner-close" onClick={() => setPdfError(null)}>×</button>
            </div>
            <div className="error-banner-message">{pdfError.message}</div>
          </div>
        </div>
      )}

      <div>
        <div className="page-heading">Plots</div>
        <div className="page-subheading">
          Upload a PDF of plots, pick the ones you want to look at, and swap the selection anytime.
        </div>
      </div>

      <div className="upload-panel">
        <div className="upload-panel-title">Upload PDF</div>
        <div className="upload-panel-hint">Accepted: .pdf</div>
        <div className="upload-slots">
          <UploadSlot
            tag="pdf"
            tagLabel="Plots"
            label="PDF of Plots"
            accept=".pdf"
            loading={pdfLoading}
            loadingLabel={
              pdfProgress.total
                ? `Rendering page ${pdfProgress.done} of ${pdfProgress.total}...`
                : "Reading PDF..."
            }
            file={pdfFileName}
            countLabel={thumbnails.length ? `${thumbnails.length} page${thumbnails.length !== 1 ? "s" : ""} loaded` : null}
            onChoose={handlePdfChange}
            disabled={pdfLoading}
          />
        </div>
      </div>

      {thumbnails.length === 0 ? (
        <div className="table-outer-wrapper">
          <div className="empty-state">
            <div className="empty-icon">🖼️</div>
            <div>Upload a PDF above to see its pages here.</div>
          </div>
          {pdfLoading && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ReactLoading type="spin" color="#003594" height={40} width={40} />
            </div>
          )}
        </div>
      ) : (
        <>
          {hasNames && (
            <div className="compare-controls">
              <div className="compare-control-group">
                <span className="compare-control-label">
                  {hasTitles
                    ? "Plots labeled using element names read from the PDF"
                    : usingBakedNames
                    ? "Plots labeled using verified names for this GBM baseline PDF (410 pages)"
                    : `Plots labeled using element names from ${modelFileName || "the uploaded model"}`}
                </span>
              </div>
              {namesAligned && (
                <div className="compare-control-group">
                  <span className="compare-control-label">Row offset:</span>
                  <input
                    type="number"
                    className="edit-select"
                    style={{ width: 60 }}
                    value={rowOffset}
                    onChange={(e) => setRowOffset(parseInt(e.target.value, 10) || 0)}
                    title="Shift the page-to-row match if plots and rows are off by a few positions"
                  />
                </div>
              )}
            </div>
          )}

          <div className="compare-controls">
            <div className="compare-control-group" style={{ flex: 1, minWidth: 220 }}>
              <input
                type="text"
                className="edit-input"
                placeholder={
                  hasNames
                    ? "Search plots by element name..."
                    : "Search plots by page number..."
                }
                value={plotSearch}
                onChange={(e) => setPlotSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="table-info">
            <span>
              {selectedPages.length} of {thumbnails.length} plot{thumbnails.length !== 1 ? "s" : ""} selected
              {plotSearch && ` — showing ${filteredThumbnails.length} match${filteredThumbnails.length !== 1 ? "es" : ""}`}
              {titlesProgress.total > 0 && titlesProgress.done < titlesProgress.total &&
                ` — reading plot names ${titlesProgress.done}/${titlesProgress.total}...`}
            </span>
            {selectedPages.length > 0 && (
              <button
                onClick={clearSelection}
                style={{ padding: "3px 10px", fontSize: 11, border: "1px solid #C8C9C7", borderRadius: 4, background: "#fff", cursor: "pointer", color: "#00205B" }}
              >
                Clear Selection
              </button>
            )}
          </div>

          {filteredThumbnails.length === 0 ? (
            <div className="table-outer-wrapper">
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <div>No plots match "{plotSearch}".</div>
              </div>
            </div>
          ) : (
            <div className="plot-gallery-split">
              <div className="plot-thumb-col">
                {filteredThumbnails.map((t) => {
                  const isSelected = selectedPages.includes(t.pageNum);
                  const order = selectedPages.indexOf(t.pageNum);
                  const label = getPlotLabel(t.pageNum);
                  return (
                    <div
                      key={t.pageNum}
                      className={`plot-thumb ${isSelected ? "selected" : ""}`}
                      onClick={() => toggleSelect(t.pageNum)}
                      title={label}
                    >
                      {isSelected && <div className="plot-thumb-check">{order + 1}</div>}
                      <img src={t.dataUrl} alt={label} />
                      <div className="plot-thumb-caption">
                        {label}
                        {hasNames && <span style={{ opacity: 0.6 }}> · p.{t.pageNum}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="plot-preview-col">
                {selectedPages.length === 0 ? (
                  <div className="table-outer-wrapper" style={{ height: "100%" }}>
                    <div className="empty-state">
                      <div className="empty-icon">👈</div>
                      <div>Click plots on the left to preview them here.</div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="zoom-row">
                      <span>Zoom:</span>
                      <input
                        type="range"
                        min="0.5"
                        max="3"
                        step="0.1"
                        value={zoom}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                      />
                      <span>{Math.round(zoom * 100)}%</span>
                    </div>

                    <div className="selected-plots-panel">
                      {selectedPages.map((pageNum, idx) => (
                        <div className="selected-plot-card" key={pageNum}>
                          <div className="selected-plot-card-header">
                            <span className="selected-plot-card-title">{getPlotLabel(pageNum)}</span>
                            <div className="selected-plot-card-actions">
                              <button className="icon-btn" onClick={() => moveSelected(pageNum, -1)} disabled={idx === 0} title="Move up">▲</button>
                              <button className="icon-btn" onClick={() => moveSelected(pageNum, 1)} disabled={idx === selectedPages.length - 1} title="Move down">▼</button>
                              <button className="icon-btn remove" onClick={() => removeSelected(pageNum)} title="Remove">Remove</button>
                            </div>
                          </div>
                          <div className="selected-plot-card-body">
                            <SelectedPlotCanvas pdfDoc={pdfDoc} pageNum={pageNum} zoom={zoom} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

// ─────────────────────────────────────────────
// MODEL EDITOR: dropdown field config (reused from BioRECIPE-style columns)
// ─────────────────────────────────────────────

const MULTI_SELECT_FIELDS = new Set(["Mechanism"]);

const DROPDOWN_FIELDS = {
  Sign: ["", "positive", "negative"],
  "Regulator Type": ["", "protein", "protein complex", "gene", "RNA", "chemical", "biological process", "protein family", "other"],
  "Regulated Type": ["", "protein", "protein complex", "gene", "RNA", "chemical", "biological process", "protein family", "other"],
  "Element Type": ["", "Gene", "Protein (amount)", "Protein (active)", "BiologicalProcess", "Chemical", "Mutation", "protein complex", "other"],
  "Regulator Database": ["", "UniProt", "HGNC", "PubChem", "Ensembl", "GENCODE", "RefSeq", "Pfam", "InterPro", "GO", "MeSH"],
  "Regulated Database": ["", "UniProt", "HGNC", "PubChem", "Ensembl", "GENCODE", "RefSeq", "Pfam", "InterPro", "GO", "MeSH"],
  "Element Database": ["", "UniProt", "HGNC", "PubChem", "Ensembl", "GENCODE", "RefSeq", "Pfam", "InterPro", "GO", "MeSH"],
  "Regulator Compartment": ["", "cytoplasm", "cytosol", "plasma membrane", "nucleus", "mitochondria", "endoplasmic reticulum", "extracellular"],
  "Regulated Compartment": ["", "cytoplasm", "cytosol", "plasma membrane", "nucleus", "mitochondria", "endoplasmic reticulum", "extracellular"],
  Compartment: ["", "cytoplasm", "cytosol", "plasma membrane", "nucleus", "mitochondria", "endoplasmic reticulum", "extracellular"],
  "Connection Type": ["", "direct", "indirect"],
  Source: ["", "expert", "literature", "database", "data"],
  Mechanism: [
    "", "binding", "phosphorylation", "dephosphorylation", "ubiquitination", "deubiquitination",
    "acetylation", "deacetylation", "methylation", "demethylation", "transcription", "translation", "translocation",
  ],
};

const WIDE_EDIT_COLUMNS = new Set([
  "Statements", "Statements List", "Comment", "Phrase",
  "Positive Regulator List", "Negative Regulator List",
  "Positive Regulation Rule", "Negative Regulation Rule",
]);

const MultiSelectDropdown = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [search, setSearch] = useState("");
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);

  const updateCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 2, left: rect.left, width: rect.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateCoords();
    setSearch("");
    const focusTimer = setTimeout(() => searchInputRef.current?.focus(), 0);
    const handleOutside = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open, updateCoords]);

  const selected = value ? value.split(",").map((s) => s.trim()).filter(Boolean) : [];

  const toggleOption = (opt) => {
    const next = selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt];
    onChange(next.join(", "));
  };

  const filteredOptions = options.filter((o) => o !== "").filter((o) => o.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="edit-multiselect">
      <div
        ref={triggerRef}
        className={`edit-multiselect-display${selected.length === 0 ? " placeholder" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        {selected.length > 0 ? selected.join(", ") : "— select —"}
      </div>
      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            className="edit-multiselect-panel"
            style={{ position: "fixed", top: coords.top, left: coords.left, minWidth: coords.width }}
          >
            <div className="edit-multiselect-search-wrap">
              <input
                ref={searchInputRef}
                type="text"
                className="edit-multiselect-search"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
              />
            </div>
            {filteredOptions.length === 0 ? (
              <div className="edit-multiselect-empty">No matches</div>
            ) : (
              filteredOptions.map((opt) => (
                <label key={opt} className="edit-multiselect-option">
                  <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggleOption(opt)} />
                  <span>{opt}</span>
                </label>
              ))
            )}
            <div
              className="edit-multiselect-option edit-multiselect-clear"
              onClick={() => { onChange(""); setOpen(false); }}
            >
              Clear selection
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

// ─────────────────────────────────────────────
// COLUMN FILTER DROPDOWN
// ─────────────────────────────────────────────

const ColumnFilterDropdown = ({
  columns,
  hiddenColumns,
  pinnedColumn,
  onToggle,
  onShowAll,
  onHideAll,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [search, setSearch] = useState("");
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectAllRef = useRef(null);

  const updateCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 220) });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateCoords();
    setSearch("");
    const focusTimer = setTimeout(() => searchInputRef.current?.focus(), 0);
    const handleOutside = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open, updateCoords]);

  // The pinned column (Element Name) never counts as hideable.
  const toggleableColumns = columns.filter((c) => c !== pinnedColumn);
  const hiddenCount = toggleableColumns.filter((c) => hiddenColumns.has(c)).length;
  const allSelected = hiddenCount === 0;
  const noneSelected = hiddenCount === toggleableColumns.length;
  const visibleCount = columns.length - hiddenCount;

  // Tri-state: indeterminate when some (but not all) columns are visible.
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = !allSelected && !noneSelected;
    }
  });

  const filteredColumns = columns.filter((c) =>
    c.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        className="upload-btn"
        style={{ padding: "5px 12px", fontSize: 12 }}
        onClick={() => setOpen((o) => !o)}
      >
        Columns ({visibleCount}/{columns.length})
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            className="edit-multiselect-panel"
            style={{ position: "fixed", top: coords.top, left: coords.left, minWidth: coords.width, maxHeight: 320 }}
          >
            <div className="edit-multiselect-search-wrap">
              <input
                ref={searchInputRef}
                type="text"
                className="edit-multiselect-search"
                placeholder="Search columns..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
              />
            </div>

            <label
              className="edit-multiselect-option"
              style={{ fontWeight: 700, borderBottom: "1px solid #C8C9C7", color: "#00205B" }}
            >
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={() => (allSelected ? onHideAll() : onShowAll())}
              />
              <span>{allSelected ? "Deselect all" : "Select all"}</span>
            </label>

            {filteredColumns.length === 0 ? (
              <div className="edit-multiselect-empty">No matches</div>
            ) : (
              filteredColumns.map((col) => {
                const isPinned = col === pinnedColumn;
                return (
                  <label
                    key={col}
                    className="edit-multiselect-option"
                    style={isPinned ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
                    title={isPinned ? "Always visible" : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={isPinned || !hiddenColumns.has(col)}
                      disabled={isPinned}
                      onChange={() => onToggle(col)}
                    />
                    <span>
                      {col}
                      {isPinned && (
                        <span style={{ fontSize: 10, color: "#B87333", marginLeft: 6 }}>
                          🔒 always visible
                        </span>
                      )}
                    </span>
                  </label>
                );
              })
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

// ─────────────────────────────────────────────
// MODEL EDITOR PAGE
// ─────────────────────────────────────────────

// Module-level constant — stable reference across renders, so `columns`
// never appears "changed" to a useEffect dependency array when no model
// is loaded. This is what was causing the infinite render loop that
// froze navigation across the whole app.
const EMPTY_ARRAY = [];

const ModelSection = ({ onModelChange }) => {
  const [modelData, setModelData] = useState(null); // { fileName, columns, rows }
  const [data, setData] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editBuffer, setEditBuffer] = useState({});
  const [filterText, setFilterText] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedCell, setExpandedCell] = useState(null); // `${rowIdx}:${col}`
  const [hiddenColumns, setHiddenColumns] = useState(new Set());

  const columns = modelData?.columns || EMPTY_ARRAY;

  // Column that can never be hidden: "Element Name" if present, else the first column.
  const pinnedColumn = useMemo(() => {
    if (!columns.length) return null;
    return columns.includes("Element Name") ? "Element Name" : columns[0];
  }, [columns]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => c === pinnedColumn || !hiddenColumns.has(c)),
    [columns, hiddenColumns, pinnedColumn],
  );

  // Push the current model (including any live edits) up to the parent so
  // the Plots and Data Results sections below can use it.
  useEffect(() => {
    onModelChange({ columns, rows: data, fileName: modelData?.fileName || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, data, modelData]);

  const handleUpload = useCallback(async (file) => {
    setLoading(true);
    setError(null);
    try {
      const parsed = await readTableFile(file);
      setModelData(parsed);
      setData(parsed.rows.map((r) => ({ ...r })));
      setEditingIndex(null);
      setEditBuffer({});
      setFilterText("");
      setHiddenColumns(new Set());
    } catch (err) {
      setError({ title: "Could not read file", message: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleColumn = useCallback((col) => {
    if (col === pinnedColumn) return;
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(col)) next.delete(col);
      else next.add(col);
      return next;
    });
  }, [pinnedColumn]);

  const showAllColumns = useCallback(() => setHiddenColumns(new Set()), []);
  const hideAllColumns = useCallback(
    () => setHiddenColumns(new Set(columns.filter((c) => c !== pinnedColumn))),
    [columns, pinnedColumn],
  );

  const filteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return data
      .map((row, i) => [i, row])
      .filter(([, row]) => {
        if (!q) return true;
        return columns.some((col) => String(row[col] ?? "").toLowerCase().includes(q));
      });
  }, [data, filterText, columns]);

  const handleEdit = useCallback((idx) => {
    setEditingIndex(idx);
    setEditBuffer({ ...data[idx] });
  }, [data]);

  const handleSave = useCallback(() => {
    const d = [...data];
    d[editingIndex] = { ...editBuffer };
    setData(d);
    setEditingIndex(null);
    setEditBuffer({});
  }, [data, editingIndex, editBuffer]);

  const handleCancel = useCallback(() => {
    setEditingIndex(null);
    setEditBuffer({});
  }, []);

  const handleDelete = useCallback((idx) => {
    if (!window.confirm("Delete this row?")) return;
    setData((d) => d.filter((_, j) => j !== idx));
    if (editingIndex === idx) {
      setEditingIndex(null);
      setEditBuffer({});
    } else if (editingIndex > idx) {
      setEditingIndex(editingIndex - 1);
    }
  }, [editingIndex]);

  const toggleInhibition = useCallback((idx) => {
    setData((d) => {
      const next = [...d];
      const row = { ...next[idx] };

      if (row.__inhibited) {
        // Turning off — restore whatever the values were before inhibition was applied.
        const snap = row.__inhibitionSnapshot || {};
        Object.keys(snap).forEach((col) => { row[col] = snap[col]; });
        delete row.__inhibitionSnapshot;
        delete row.__inhibited;
      } else {
        // Turning on — remember current values, then zero/clear the relevant columns.
        const snap = {};
        columns.forEach((col) => {
          if (col.startsWith("State List")) snap[col] = row[col];
        });
        if (columns.includes("Negative Regulation Rule")) snap["Negative Regulation Rule"] = row["Negative Regulation Rule"];
        if (columns.includes("Positive Regulation Rule")) snap["Positive Regulation Rule"] = row["Positive Regulation Rule"];

        Object.keys(snap).forEach((col) => {
          row[col] = col.startsWith("State List") ? "0" : "";
        });
        row.__inhibitionSnapshot = snap;
        row.__inhibited = true;
      }

      next[idx] = row;
      return next;
    });
  }, [columns]);

  const handleInsertRow = useCallback((idx) => {
    if (editingIndex !== null) return;
    const newRow = {};
    columns.forEach((c) => { newRow[c] = ""; });
    const insertAt = idx + 1;
    const d = [...data];
    d.splice(insertAt, 0, newRow);
    setData(d);
    setEditingIndex(insertAt);
    setEditBuffer({ ...newRow });
  }, [data, columns, editingIndex]);

  const handleAddRow = useCallback(() => {
    if (editingIndex !== null) return;
    const newRow = {};
    columns.forEach((c) => { newRow[c] = ""; });
    setData((d) => [...d, newRow]);
    setEditingIndex(data.length);
    setEditBuffer({ ...newRow });
  }, [data, columns, editingIndex]);

  const handleDownload = () => {
    if (!data.length) return;
    const exp = data.map((r) => {
      const o = {};
      columns.forEach((c) => { o[c] = r[c] ?? ""; });
      return o;
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exp, { header: columns });
    ws["!cols"] = columns.map((c) => ({ wch: Math.max(c.length + 2, 12) }));
    XLSX.utils.book_append_sheet(wb, ws, "Model");
    const today = new Date().toISOString().slice(0, 10);
    const base = (modelData?.fileName || "model").replace(/\.[^.]+$/, "");
    XLSX.writeFile(wb, `${base}_edited_${today}.xlsx`);
  };

  return (
    <>
      {error && (
        <div className="error-banner">
          <div className="error-banner-content">
            <div className="error-banner-header">
              <span>❌ {error.title}</span>
              <button className="error-banner-close" onClick={() => setError(null)}>×</button>
            </div>
            <div className="error-banner-message">{error.message}</div>
          </div>
        </div>
      )}

      <div>
        <div className="page-heading" style={{ fontSize: 18 }}>Model</div>
        <div className="page-subheading">
          Upload a model file and edit it directly — add, insert, or delete rows. This model feeds the Plots and Data Results sections below.
        </div>
      </div>

      <div className="upload-panel">
        <div className="upload-panel-title">Upload Model</div>
        <div className="upload-panel-hint">Accepted: .xlsx, .xls, .csv</div>
        <div className="upload-slots">
          <UploadSlot
            tag="model"
            tagLabel="Model"
            label="Model to Edit"
            accept=".xlsx,.xls,.csv"
            loading={loading}
            file={modelData?.fileName}
            countLabel={modelData ? `${data.length} row${data.length !== 1 ? "s" : ""} loaded` : null}
            onChoose={handleUpload}
            disabled={loading}
          />
        </div>
      </div>

      {!modelData ? (
        <div className="table-outer-wrapper">
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <div>Upload a model file above to start editing.</div>
          </div>
        </div>
      ) : (
        <>
          <div className="table-info">
            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
              <input
                type="text"
                className="edit-input"
                style={{ maxWidth: 280 }}
                placeholder="Search rows..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
              <ColumnFilterDropdown
                columns={columns}
                hiddenColumns={hiddenColumns}
                pinnedColumn={pinnedColumn}
                onToggle={toggleColumn}
                onShowAll={showAllColumns}
                onHideAll={hideAllColumns}
              />
              <span>
                Showing {filteredRows.length} of {data.length} row{data.length !== 1 ? "s" : ""}
                {hiddenColumns.size > 0 && ` · ${visibleColumns.length} of ${columns.length} columns shown`}
              </span>
            </div>
            <button className="download-btn" onClick={handleDownload} disabled={!data.length}>
              <Img src={download_logo} /> Download Edited Model
            </button>
          </div>

          <div className="table-outer-wrapper">
            <table className="bio-table">
              <thead>
                <tr>
                  <th className="key-th" style={{ backgroundColor: "#ccc", color: "#00205B" }}>#</th>
                  <th className="status-th">Actions</th>
                  {visibleColumns.map((col) => (
                    <th key={col} style={{ minWidth: WIDE_EDIT_COLUMNS.has(col) ? 260 : Math.max(140, col.length * 8 + 32) }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(([originalIndex, row], i) => {
                  const isEditing = editingIndex === originalIndex;
                  return (
                    <tr key={originalIndex} className={isEditing ? "editing" : ""}>
                      <td className="row-num-td">{i + 1}</td>
                      <td className="actions-td">
                        {isEditing ? (
                          <>
                            <button className="action-btn save-btn" onClick={handleSave}>Save</button>
                            <button className="action-btn cancel-btn" onClick={handleCancel}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button
                              className={`action-btn inhibit-btn ${row.__inhibited ? "active" : ""}`}
                              onClick={() => toggleInhibition(originalIndex)}
                              disabled={editingIndex !== null}
                              title={
                                row.__inhibited
                                  ? "Turn off inhibition and restore the original values"
                                  : "Zero out all State List columns and clear both Regulation Rule columns for this row"
                              }
                              style={editingIndex !== null ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                            >
                              {row.__inhibited ? "Inhibited ✓" : "Inhibition"}
                            </button>
                            <button
                              className="action-btn edit-btn"
                              onClick={() => handleEdit(originalIndex)}
                              disabled={editingIndex !== null}
                              style={editingIndex !== null ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                            >
                              Edit
                            </button>
                            <button
                              className="action-btn insert-btn"
                              onClick={() => handleInsertRow(originalIndex)}
                              disabled={editingIndex !== null}
                              title="Insert a new row below this one"
                              style={editingIndex !== null ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                            >
                              + Insert
                            </button>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => handleDelete(originalIndex)}
                              disabled={editingIndex !== null}
                              style={editingIndex !== null ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                      {visibleColumns.map((col) => {
                        if (isEditing) {
                          if (MULTI_SELECT_FIELDS.has(col)) {
                            return (
                              <td key={col} style={{ padding: "4px 8px" }}>
                                <MultiSelectDropdown
                                  options={DROPDOWN_FIELDS[col] || []}
                                  value={editBuffer[col] ?? ""}
                                  onChange={(v) => setEditBuffer((p) => ({ ...p, [col]: v }))}
                                />
                              </td>
                            );
                          }
                          if (DROPDOWN_FIELDS[col]) {
                            return (
                              <td key={col} style={{ padding: "4px 8px" }}>
                                <select
                                  className="edit-select"
                                  value={editBuffer[col] ?? ""}
                                  onChange={(e) => setEditBuffer((p) => ({ ...p, [col]: e.target.value }))}
                                  onKeyDown={(e) => { if (e.key === "Escape") handleCancel(); }}
                                >
                                  {DROPDOWN_FIELDS[col].map((opt) => (
                                    <option key={opt} value={opt}>{opt === "" ? "— select —" : opt}</option>
                                  ))}
                                </select>
                              </td>
                            );
                          }
                          if (WIDE_EDIT_COLUMNS.has(col)) {
                            return (
                              <td key={col} style={{ padding: "4px 8px" }}>
                                <textarea
                                  className="edit-textarea"
                                  value={editBuffer[col] ?? ""}
                                  onChange={(e) => setEditBuffer((p) => ({ ...p, [col]: e.target.value }))}
                                  onKeyDown={(e) => { if (e.key === "Escape") handleCancel(); }}
                                />
                              </td>
                            );
                          }
                          return (
                            <td key={col} style={{ padding: "4px 8px" }}>
                              <input
                                className="edit-input"
                                type="text"
                                value={editBuffer[col] ?? ""}
                                onChange={(e) => setEditBuffer((p) => ({ ...p, [col]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleSave();
                                  if (e.key === "Escape") handleCancel();
                                }}
                              />
                            </td>
                          );
                        }

                        // View mode
                        const cellKey = `${originalIndex}:${col}`;
                        const isExpanded = expandedCell === cellKey;
                        if (WIDE_EDIT_COLUMNS.has(col)) {
                          return (
                            <td
                              key={col}
                              className={`statements-cell${isExpanded ? " expanded" : ""}`}
                              onClick={() => setExpandedCell(isExpanded ? null : cellKey)}
                              title={isExpanded ? undefined : String(row[col] ?? "")}
                            >
                              <div style={{ whiteSpace: isExpanded ? "normal" : "nowrap", overflow: isExpanded ? "visible" : "hidden", textOverflow: isExpanded ? "unset" : "ellipsis" }}>
                                {row[col]}
                              </div>
                            </td>
                          );
                        }
                        return (
                          <td key={col} title={String(row[col] ?? "")}>
                            {row[col]}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="button-row">
            <button
              className="add-row-btn"
              onClick={handleAddRow}
              disabled={editingIndex !== null}
              style={editingIndex !== null ? { opacity: 0.5, cursor: "not-allowed" } : {}}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Row
            </button>
          </div>
        </>
      )}
    </>
  );
};
// ─────────────────────────────────────────────
// SIDE NOTE PANEL
// ─────────────────────────────────────────────

const NOTE_STORAGE_KEY = "analyzer_side_note";

const SideNotePanel = () => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(NOTE_STORAGE_KEY);
      if (saved) setNote(saved);
    } catch (e) {
      // ignore — localStorage may be unavailable
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setNote(val);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(NOTE_STORAGE_KEY, val);
        setSavedAt(new Date());
      } catch (e) {
        // ignore
      }
    }, 500);
  };

  const handleClear = () => {
    if (!window.confirm("Clear this note?")) return;
    setNote("");
    try {
      localStorage.removeItem(NOTE_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
    setSavedAt(null);
  };

  const handleDownload = () => {
    const blob = new Blob([note], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `notes_${today}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <button
        className={`note-toggle-btn ${open ? "active" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        📝 Notes
      </button>

      {open && (
        <aside className="side-note-panel">
          <div className="side-note-header">
            <span>📝 Notes</span>
            <div className="side-note-header-actions">
              {note && <button className="side-note-clear" onClick={handleDownload} title="Download as .txt">⬇</button>}
              {note && <button className="side-note-clear" onClick={handleClear}>Clear</button>}
              <button className="side-note-close" onClick={() => setOpen(false)} title="Close">×</button>
            </div>
          </div>
          <textarea
            className="side-note-textarea"
            placeholder="Jot down anything worth remembering while you work — element names to check, questions, next steps..."
            value={note}
            onChange={handleChange}
            autoFocus
          />
          {savedAt && (
            <div className="side-note-saved">
              ✓ Saved {savedAt.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </aside>
      )}
    </>
  );
};

// ─────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────

const AnalyzerAPP = () => {
  const [modelInfo, setModelInfo] = useState({ columns: [], rows: [], fileName: null });

  return (
    <>
      <style>{styles}</style>
      <div className="page-wrapper">
        <header className="top-header">
          <div className="top-header-logo" />
          <div className="top-header-title">Analyzer</div>
        </header>
        <SideNotePanel />
        <main className="main-area main-area-full">
          <ModelSection onModelChange={setModelInfo} />

          <div style={{ borderTop: "2px solid #C8C9C7", margin: "8px 0" }} />
          <PlotSection
            modelRows={modelInfo.rows}
            modelColumns={modelInfo.columns}
            modelFileName={modelInfo.fileName}
          />
        </main>
        
      </div>
    </>
  );
};

export default AnalyzerAPP;